import React, { useEffect } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  StandardTextFieldProps,
} from '@mui/material';
import { useIMask } from 'react-imask';
import { MoFieldErrorText } from '../MoFieldErrorText';
import { MoFieldLabel } from '../MoFieldLabel';

export interface MoTextInputProps extends Omit<
  StandardTextFieldProps,
  'onKeyDown' | 'onKeyUp' | 'onInvalid' | 'variant'
> {
  name: string;
  label: string;
  helperText?: string | string[];
  hideLabel?: boolean;
  floatingLabel?: boolean;
  labelAction?: React.ReactNode;
}

export function MoTextInput({
  name,
  label,
  className,
  helperText,
  error,
  InputProps,
  inputProps,
  hideLabel,
  onChange,
  floatingLabel = true,
  labelAction,
  ...props
}: MoTextInputProps) {
  let errorMessage: string | null = null;
  if (helperText) {
    errorMessage = typeof helperText === 'string' ? helperText : helperText[0];
  }

  const isTelInput = props.type === 'tel';

  const {
    ref: telRef,
    maskRef,
    value: maskValue,
  } = useIMask(
    {
      mask: [
        {
          mask: '(#00) 000-0000',
          definitions: {
            '#': /[1-9]/,
          },
        },
        {
          mask: '+1 (#00) 000-0000',
          definitions: {
            '#': /[1-9]/,
          },
        },
      ],
    },
    {
      onAccept: (value: string) => {
        if (onChange) onChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
      },
    },
  );

  useEffect(() => {
    if (!isTelInput) return;
    const mask = maskRef.current;
    if (!mask) return;
    const externalValue = props.value == null ? '' : String(props.value);
    if (externalValue === mask.value || externalValue === mask.unmaskedValue) return;
    mask.value = externalValue;
  }, [isTelInput, props.value, maskRef]);

  const ref = isTelInput ? telRef : null;

  return (
    <FormControl fullWidth>
      {!hideLabel && (
        <>
          {floatingLabel ? (
            <InputLabel htmlFor={name} error={error}>
              {label}
            </InputLabel>
          ) : (
            <MoFieldLabel label={label} htmlFor={name} error={error} action={labelAction} />
          )}
        </>
      )}
      <OutlinedInput
        {...props}
        {...InputProps}
        {...(isTelInput ? { value: maskValue } : null)}
        inputProps={{
          ...inputProps,
          id: name,
          'aria-label': label,
          ref: ref || inputProps?.ref,
        }}
        error={error}
        name={name}
        margin="none"
        label={floatingLabel ? label : undefined}
        onChange={isTelInput ? undefined : onChange}
      />
      {!error && helperText && (
        <FormHelperText component="div" sx={{ ml: 0 }}>
          {helperText}
        </FormHelperText>
      )}
      {error && errorMessage && <MoFieldErrorText>{errorMessage}</MoFieldErrorText>}
    </FormControl>
  );
}
